import React from 'react';
import styled from 'styled-components';

export interface ToggleGroupOption<TId extends string> {
  id: TId;
  /** Segment label — Title Case per the design system. */
  label: string;
  /** Optional count rendered after the label, muted ("Open 3"). */
  count?: number;
  /**
   * Overrides the group's `tone` for this segment's active fill. For a group
   * whose segments aren't peers: one is the unfiltered resting view and the
   * others narrow it, so only the narrowing ones should take the applied-filter
   * colour (the homepage My Content / All Content scope). Defaults to `tone`.
   */
  activeTone?: 'neutral' | 'accent';
}

interface ToggleGroupProps<TId extends string> {
  options: ToggleGroupOption<TId>[];
  value: TId;
  onChange: (id: TId) => void;
  ariaLabel?: string;
  /** Per-segment test ids, rendered as `${testIdPrefix}-${option.id}`. */
  testIdPrefix?: string;
  /**
   * Locks the whole group — every segment takes the real `disabled` attribute,
   * so a keyboard reaches none of them, and the track dims. For a choice that
   * is settled while something acts on it (a form mid-request), where hiding
   * the control would lose the answer the creator gave.
   */
  disabled?: boolean;
  /**
   * Which selection colour the active segment takes.
   *
   * - `neutral` (default) — a `--surface-elevated` fill with brighter text. For
   *   a **view filter**: which slice of a list is on screen (comment status).
   * - `accent` — a quiet gold tint with gold text. For a control that steers
   *   **creative or visual output**, which the design system colours gold
   *   throughout (checkboxes, switches, sliders): the Translation Review's
   *   Paragraph / Segment / Words control, and the Auto-Chapter overlay's
   *   fields. A tint rather than a solid fill, because gold at full strength on
   *   a segment reads as a primary action rather than as a setting.
   *
   * Neither is green: `--primary` is for actions, and a segmented control is
   * never the thing being confirmed.
   */
  tone?: 'neutral' | 'accent';
  /** Extra classes, so a host can place the group (it sizes to its content). */
  className?: string;
  /** Passed through when the group is standing in for a radiogroup. */
  role?: string;
}

/**
 * Single-select segmented control for switching between a few mutually
 * exclusive views of one list (e.g. comment status: Open / Resolved / All).
 * Exactly one segment is always active — unlike the multi-select checkbox
 * dropdown used by the homepage Type / Clip Status filters, which suits
 * longer option lists and combinable criteria.
 *
 * See docs/design-system.md "Toggle Group (Segmented Filter)".
 */
export function ToggleGroup<TId extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  testIdPrefix,
  disabled = false,
  tone = 'neutral',
  className,
  role = 'group',
}: ToggleGroupProps<TId>) {
  return (
    <Group
      role={role}
      aria-label={ariaLabel}
      className={className}
      $disabled={disabled}
    >
      {options.map(option => {
        const active = option.id === value;
        return (
          <Segment
            key={option.id}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            $active={active}
            $tone={option.activeTone ?? tone}
            data-testid={
              testIdPrefix ? `${testIdPrefix}-${option.id}` : undefined
            }
            onClick={() => onChange(option.id)}
          >
            {option.label}
            {option.count !== undefined && <Count>{option.count}</Count>}
          </Segment>
        );
      })}
    </Group>
  );
}

export default ToggleGroup;

// Recessed track holding the segments (the input-fill token keeps it one step
// above the panel background, like a resting text field).
//
// `align-self: flex-start` so a column-flex host — which every dialog field is —
// cannot stretch the track across the card. A segmented control sizes to its
// options; stretched, the two segments no longer read as a pair of equal
// answers and the inactive one becomes an empty bar.
const Group = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-self: flex-start;
  gap: 2px;
  padding: 2px;
  background: var(--input-background);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 6px);
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
`;

// One segment. The active state is a fill with no border or outline — the
// app's "fill only" selection emphasis — in whichever of the two tones the
// control's job calls for.
const Segment = styled.button<{
  $active: boolean;
  $tone: 'neutral' | 'accent';
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border: none;
  border-radius: 4px;
  background: ${props =>
    !props.$active
      ? 'transparent'
      : props.$tone === 'accent'
        ? 'var(--accent-tint)'
        : 'var(--surface-elevated)'};
  color: ${props =>
    !props.$active
      ? 'var(--text-muted)'
      : props.$tone === 'accent'
        ? 'var(--accent)'
        : 'var(--foreground)'};
  font-size: var(--text-xs, 12px);
  font-weight: ${props =>
    props.$active ? 'var(--font-weight-medium, 500)' : 'normal'};
  cursor: pointer;
  transition:
    color var(--transition-fast, 0.15s) ease,
    background var(--transition-fast, 0.15s) ease;

  // An inactive segment escalates to full-strength text; an active one is
  // already at its colour and must not change under the pointer, or the hover
  // reads as "this would do something".
  &:hover:not(:disabled) {
    color: ${props =>
      !props.$active
        ? 'var(--foreground)'
        : props.$tone === 'accent'
          ? 'var(--accent)'
          : 'var(--foreground)'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

// Muted count after the label; tabular so segments don't shift width as
// counts change.
const Count = styled.span`
  color: var(--text-muted);
  font-weight: normal;
  font-variant-numeric: tabular-nums;
`;
